/** ADM-OPS-S010 task-backed completion matrix. Browser never translates or writes cells directly. */
import { useConfigTranslationTasks } from '~/hooks/use-i18n-translate';
import { Icons } from '~/lib/icons';
import { LangChip } from '~/ui/LangChip';
import { Section } from '~/ui/Section';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { errorToast, useCanUpsertOps } from './use-ops-mutations';

const MODULE_LABELS: Record<string, string> = {
  home_sections: '首页推荐分区标题',
  faq: 'FAQ',
  categories: '书籍分类',
  tags: '书籍标签',
  product_titles: '商品名称',
  push_templates: '推送模板文案',
};

export function OpsFillMatrix() {
  const { tasks, isLoading, retry } = useConfigTranslationTasks();
  const canRetry = useCanUpsertOps();
  const success = tasks.filter((task) => task.status === 'success').length;
  const failed = tasks.filter((task) => task.status === 'failed').length;

  const retryTask = (id: string) => {
    retry.mutate(id, {
      onSuccess: () => toast('补齐单元已重新排队', { tone: 'pos', icon: 'IconRefresh' }),
      onError: (error) => errorToast(error, '重新排队失败'),
    });
  };

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="card" style={{ padding: '12px 14px', background: 'var(--info-soft)' }}>
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconInfo size={13} style={{ color: 'var(--info)', marginTop: 2 }} />
          <div style={{ fontSize: 11.5, lineHeight: 1.6 }}>
            矩阵直接读取服务端补齐任务。翻译、cell 写入和任务终态由 worker
            按单元事务提交；后台只允许把
            <span className="mono"> failed </span>单元重新排队。
          </div>
        </div>
      </div>
      <Section
        title="补齐矩阵"
        sub={
          isLoading
            ? '正在读取任务…'
            : `${tasks.length} 单元 · success ${success} / pending ${tasks.length - success - failed} / failed ${failed}`
        }
      >
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 190 }}>module</th>
              <th style={{ width: 130 }}>目标语言</th>
              <th style={{ width: 105 }}>status</th>
              <th className="num" style={{ width: 90 }}>
                未译格数
              </th>
              <th>失败原因</th>
              <th style={{ width: 155 }}>触发来源</th>
              <th className="num" style={{ width: 70 }}>
                次数
              </th>
              <th style={{ width: 92 }} />
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.id}>
                <td>
                  <div className="mono" style={{ fontSize: 11.5 }}>
                    {task.module}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {MODULE_LABELS[task.module] ?? task.module}
                  </div>
                </td>
                <td>
                  <LangChip code={task.target_language} style={{ fontSize: 10 }} />
                </td>
                <td>
                  <span
                    className={`chip ${task.status === 'success' ? 'chip-pos' : task.status === 'failed' ? 'chip-neg' : 'chip-info'} mono`}
                    style={{ fontSize: 10 }}
                  >
                    {task.status}
                  </span>
                </td>
                <td className="num mono">{task.missing_cell_count}</td>
                <td
                  style={{
                    fontSize: 11,
                    color: task.failure_reason ? 'var(--neg)' : 'var(--fg-faint)',
                  }}
                >
                  {task.failure_reason ?? '—'}
                </td>
                <td className="mono" style={{ fontSize: 10.5 }}>
                  {task.trigger_source}
                </td>
                <td className="num mono">{task.attempt_count}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={!canRetry || task.status !== 'failed' || retry.isPending}
                    onClick={() => retryTask(task.id)}
                  >
                    <Icons.IconRefresh size={11} />
                    retry
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>
    </div>
  );
}
